import React, { useState } from 'react';
import {
  Container, Typography, Box, Paper, Button, Alert, CircularProgress,
  List, ListItem, ListItemText, Divider
} from '@mui/material';
import { apiService } from '../services/api';
import PageLayout from '../components/Layout/PageLayout';
import BackButton from '../components/BackButton';

const ApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const testApiConnection = async () => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      // 測試獲取日報列表
      const dailyReports = await apiService.getDailyReports({ page: 1, pageSize: 5 });
      setResults(prev => [...prev, {
        test: '獲取日報列表',
        success: true,
        data: `找到 ${dailyReports.items?.length || 0} 條記錄`
      }]);

    //   // 測試獲取站點類型
    //   const siteTypes = await apiService.get('/dailyreport/site-types');
    //   setResults(prev => [...prev, {
    //     test: '獲取站點類型',
    //     success: true,
    //     data: `站點類型: ${siteTypes.map((st: any) => st.label).join(', ')}`
    //   }]);

      // 測試獲取模板
      const template = await apiService.getDailyReportTemplate('Full Time');
      setResults(prev => [...prev, {
        test: '獲取 Full Time 模板',
        success: true,
        data: `模板項目數量: ${template.items?.length || 0}`
      }]);

    } catch (err: any) {
      setError(err.message || 'API 測試失敗');
      setResults(prev => [...prev, {
        test: 'API 測試',
        success: false,
        data: err.message || '未知錯誤'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const testCreateDailyReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const testData = {
        reportSiteType: 'Full Time',
        contractNo: 'TEST-2024-001',
        workOrderNo: 'TEST-WO-001',
        workOrderPeriod: '測試期間',
        project: 'API 測試項目',
        reportDate: new Date().toISOString().split('T')[0],
        items: Array.from({ length: 16 }, (_, i) => ({
          id: i + 1,
          code: `TEST-${i + 1}`,
          description: `測試項目 ${i + 1}`,
          quantity: Math.floor(Math.random() * 10) + 1,
          unit: '個'
        })),
        temperature: {
          max1: '25', max2: '26', max3: '27', max4: '28', maxC: '29', maxR: '30',
          min1: '15', min2: '16', min3: '17', min4: '18', minC: '19', minR: '20'
        }
      };

      const result = await apiService.createDailyReport(testData);
      setResults(prev => [...prev, {
        test: '創建測試日報',
        success: true,
        data: `創建成功，ID: ${result.id}`
      }]);

    } catch (err: any) {
      setError(err.message || '創建測試日報失敗');
      setResults(prev => [...prev, {
        test: '創建測試日報',
        success: false,
        data: err.message || '未知錯誤'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout title="API 連接測試">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          API 連接測試
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          測試前端與後端 DailyReport API 的連接狀態
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              onClick={testApiConnection}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              測試 API 連接
            </Button>
            <Button
              variant="outlined"
              onClick={testCreateDailyReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              測試創建日報
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {results.length > 0 && (
            <List>
              {results.map((result, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={result.test}
                      secondary={result.data}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: result.success ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </ListItem>
                  {index < results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            測試說明
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <strong>測試 API 連接</strong>: 測試基本的 API 端點是否可訪問
            <br />
            • <strong>測試創建日報</strong>: 測試創建日報功能是否正常工作
            <br />
            • 如果測試失敗，請檢查後端服務器是否正在運行
            <br />
            • 確保後端 API 地址配置正確（當前: http://localhost:5139/api）
          </Typography>
        </Paper>
      </Container>
    </PageLayout>
  );
};

export default ApiTest;